import Vue from "vue";
import { Store } from "vuex";
import { Action, Module, Mutation, VuexModule, getModule } from "vuex-module-decorators";

import { matchesCaseInsensitive } from "@/utils/comparators/string";
import emptyFetchStateFactory from "@/utils/factories/fetch-state";
import logger, { logLevel } from "@/utils/logger";
import { filterRemoveUndefined, shuffle } from "@/utils/transform/array";
import mapData from "@/utils/transform/map-data";
import { RequiredPartial } from "@/utils/types";

export const DEFAULT_POST_LIMIT = 20;
export const DEFAULT_NONPOST_LIMIT = 3;
const popularSearches = [
  "Clean Beauty",
  "Moisturizer",
  "Mascara",
  "Nail Polish",
  "Curling Iron",
  "High Chair",
  "Nursery",
  "Playroom",
  "Stroller",
  "Carseat",
  "Baby Carrier",
  "Diaper Bag",
  "White Dress",
  "Over the Knee Boots",
  "Ankle Boots",
  "Sweaters",
  "Skirts",
  "Denim Shorts",
  "Moto Jacket",
  "Cocktail Dress",
  "Clutch",
  "Pea Coat",
  "Gingham",
  "Sundress",
  "Cardigans",
  "Athletic",
  "Leopard Print",
  "Polka Dots",
  "Trench Coat",
  "Gucci",
  "Stuart Weitzman Highland",
  "Heels",
  "Kitchen",
  "Unicorn Float",
  "Marble table",
  "Bar Cart",
  "Patio",
  "Rugs",
  "Candle",
  "Letterboard",
] as const;

export const localStorageKeyMap: Readonly<{ [key in SearchType]: string }> = {
  post: "previousSearches",
  profile: "previousCreators",
} as const;

// Types for store actions to be passed to the search service
type PartialSearchClickEvent = { type: SearchType } & RequiredPartial<SearchClickEvent, RequiredClickKeys>;

// Structure for Hard searches (search results)
interface SearchMap<T extends SearchType> {
  active: ActiveSearchData<SearchResultType<T>>;
  // No discrete interface to import, using the supplied type on the vue instance instead
  fetchState: Vue["$fetchState"];
  // Queued up a request after the current search has completed. Volatile
  pendingStack: QueuedSearch[];
}

// Structure for Soft searches (suggestions)
interface SuggestionMap<T extends SuggestionType> {
  active: ActiveSearchData<SearchResultType<T>>;
  fetchState: Vue["$fetchState"];
}

interface SetSearchResultsBaseProps<T extends QueryableIndex> {
  type: T;
  results: LtkSearchResponse<T>;
  isSuggestion?: boolean;
  locale: string;
}

interface SetSearchResultsProps<T extends SearchType> extends SetSearchResultsBaseProps<T> {
  isSuggestion?: false;
}
interface SetSuggestionResultsProps<T extends SuggestionType> extends SetSearchResultsBaseProps<T> {
  isSuggestion: true;
}

// Purposely untyped to let inference determine if the type overlaps with the expected ActiveSearchData type
export const emptyActiveSetFactory = () => ({
  ids: [],
  map: {},
  total: 0,
  query: "",
  queryID: "",
  indexName: "",
});

export const emptySearchSetFactory = <T extends SearchType>() =>
  <SearchMap<T>>{ active: emptyActiveSetFactory(), fetchState: emptyFetchStateFactory(), pendingStack: [] };

export const emptySuggestionSetFactory = <T extends SuggestionType>() =>
  <SuggestionMap<T>>{ active: emptyActiveSetFactory(), fetchState: emptyFetchStateFactory() };

export const isSearchResultProfile = (
  type: QueryableIndex,
  item: SearchHitProfile | undefined,
): item is SearchHitProfile =>
  item !== undefined && ((item as SearchHitProfile).type === "profile" || type === "profile");

@Module({
  name: "search",
  stateFactory: true,
  namespaced: true,
})
export default class SearchStore extends VuexModule {
  searchesMap: Record<SearchType, SearchMap<SearchType>> = {
    post: emptySearchSetFactory<"post">(),
    profile: emptySearchSetFactory<"profile">(),
  };

  suggestionsMap: Record<SuggestionType, SuggestionMap<SuggestionType>> = {
    profile: emptySuggestionSetFactory<"profile">(),
    suggestion: emptySuggestionSetFactory<"suggestion">(),
  };

  pendingSuggestions: QueuedSuggestion[] = [];

  // Previous Searches
  historyMap: Record<SearchType, SearchSuggestion[]> = {
    post: [],
    profile: [],
  };

  get popularSearches() {
    return shuffle(popularSearches, DEFAULT_NONPOST_LIMIT).map(
      (item) =>
        <SearchSuggestion>{
          query: item,
          objectID: item,
        },
    );
  }

  get searchTypeItem() {
    return <T extends SearchType>(type: T): SearchMap<T> => this.searchesMap[type];
  }

  get suggestionTypeItem() {
    return <T extends SuggestionType>(type: T): SuggestionMap<T> => this.suggestionsMap[type];
  }

  get historyTypeItem() {
    return <T extends SearchType>(type: T): SearchSuggestion[] => this.historyMap[type];
  }

  get searchTypeItemHasMorePages() {
    return (type: SearchType) => {
      const { ids, total } = this.searchTypeItem(type).active;
      return ids.length !== total;
    };
  }

  get isQueryInSuggestions() {
    return (type: SuggestionType, query: string) =>
      this.formattedSuggestions(type).findIndex((item) => item.query === query) !== -1;
  }

  get searchResultsProfileIds() {
    return (type: SearchType) =>
      this.searchTypeItem(type)
        .active.ids.map((id) => this.searchTypeItem(type).active.map[id])
        .map((item) => (item?.type === "post" ? item.postProfileId : item?.objectID)) // (item && "display_name" in item ? item.display_name : item?.postProfileDisplay))
        .filter(filterRemoveUndefined);
  }

  get isQueryInHistory() {
    return (type: SearchType, query: string) =>
      this.historyTypeItem(type).findIndex((item) => item.query === query) !== -1;
  }

  get formattedSuggestions() {
    return (type: SuggestionType) =>
      this.suggestionTypeItem(type)
        .active.ids.map((id) => this.suggestionTypeItem(type).active.map[id])
        .filter(filterRemoveUndefined)
        .map((item) => {
          const avatarUrl = "imageLink" in item ? item.imageLink : undefined;
          const query = typeof item?.postProfileDisplay === "string" ? item?.postProfileDisplay : item?.query;
          return <SearchSuggestion>{ objectID: item.objectID, query, avatarUrl };
        });
  }

  @Action({ rawError: true })
  fetchSearchHistory() {
    if (process.server) {
      return;
    }

    // Setting state _after_ hydration replaces the sate from the server. Since this data is stored between sessions in localStorage,
    // it must be initialized client side and after server state has been set or else it will be emptied out
    (["post", "profile"] as const).forEach((type) => {
      const data = localStorage.getItem(localStorageKeyMap[type]);
      let response: SearchSuggestion[] = [];
      try {
        response = JSON.parse(data ?? "[]");
      } catch (error) {
        logger(logLevel.WARN, "Malformed JSON for search history", { type, data }, error);
      }

      if (Array.isArray(response) === false) {
        logger(logLevel.LOG, "Unexpected response format. Expected array", { response, type });
        return;
      }

      const searchHistory = response.slice(0, DEFAULT_NONPOST_LIMIT);

      this.setSearchHistory({ type, searchHistory });
    });
  }

  @Action({ rawError: true })
  addSearchHistory({ searchItem, type }: { searchItem: SearchSuggestion; type: SearchType }) {
    // We can't save previous searches on the server, and it causes an error
    if (process.server || !searchItem) {
      return;
    }

    const locale = this.store.$i18n.locale;

    // Remove an existing entry from the existing history as it will get promoted to the beginning of the list
    const searchHistory = this.historyTypeItem(type)
      .filter(
        ({ objectID, query }) =>
          matchesCaseInsensitive(objectID, searchItem.objectID, locale) === false &&
          matchesCaseInsensitive(query, searchItem.query, locale) === false,
      )
      // Slice off one more than needed to allow for adding the new item at the beginning
      .slice(0, DEFAULT_NONPOST_LIMIT - 1);

    searchHistory.unshift(searchItem);

    this.setSearchHistory({ searchHistory, type });
  }

  @Mutation
  setSearchHistory({ searchHistory, type }: { searchHistory: SearchSuggestion[]; type: SearchType }) {
    Vue.set(this.historyMap, type, searchHistory);
    localStorage.setItem(localStorageKeyMap[type], JSON.stringify(searchHistory));
  }

  /**
   * Search Result Actions
   */

  /**
   * Should be consitered the initial entrypoint for searching. This action will initiate a search if
   * one is not already active for that type, and will proceed to queue same-type searches. Once the
   * current search is done, the _last_ queued search of that type will be used to search again.
   * This process will help reduce concurrent search requests from consuming bandwidth, while reducing
   * the business logic needed for checking that a returned search matches current store state, as there
   * should only ever be one per type in flight at a time.
   *
   * Because of the nature of this action, awaiting a promise from it will be unreliable for determining
   * pending state of searching. Instead, a nuxt fetchState structure is available for this search type to
   * give loading status of the searches.
   */
  @Action({ rawError: true })
  async queueSearch<T extends SearchType>({ query, type }: { query: string; type: T }) {
    const searchSet = this.searchesMap[type];

    if (searchSet.fetchState.pending === true) {
      this.setQueuedSearch({ type, query });
      return;
    }

    this.lockSearch({ type });

    try {
      await this.fetchSearch({ query, type });
    } finally {
      this.unlockSearch({ type });
    }

    // empty the queue, taking only the last item in it
    const nextSearch = searchSet.pendingStack.length > 0 ? { ...searchSet.pendingStack[0] } : undefined;
    this.clearQueuedSearches({ type });
    if (nextSearch !== undefined) {
      // Rerun the next item in the queue
      await this.queueSearch({ type, ...nextSearch });
    }
  }

  @Action({ rawError: true })
  async queueSearchSuggestions({
    query,
    types = ["suggestion", "profile"],
  }: {
    query: string;
    types: SuggestionType[];
  }) {
    const hasLock = types.some((type) => this.suggestionTypeItem(type).fetchState.pending);
    if (hasLock === true) {
      this.setQueuedSuggestion({ types, query });
      return;
    }

    types.forEach((type) => this.lockSearch({ type, isSuggestion: true }));

    try {
      await this.fetchSearchSuggestions({ query, types });
    } finally {
      types.forEach((type) => this.unlockSearch({ type, isSuggestion: true }));
    }

    // empty the queue, taking only the last item in it
    const nextSearch = this.pendingSuggestions.length > 0 ? { ...this.pendingSuggestions[0] } : undefined;
    this.clearQueuedSuggestions();
    if (nextSearch !== undefined) {
      // Rerun the next item in the queue
      await this.queueSearchSuggestions(nextSearch);
    }
  }

  @Action({ rawError: true })
  async fetchSearchSuggestions({ query, types }: { query: string; types: SuggestionType[] }) {
    const indexMap: SearchParameters[] = types.map((type) => ({
      type,
      hitsPerPage: DEFAULT_NONPOST_LIMIT,
    }));

    let response = {} as LtkMultipleSearchResponse<"suggestion"> | undefined;
    try {
      response = await this.store.$search.searchMany({ rawQuery: query, indexMap });
    } catch (error) {
      logger(logLevel.ERROR, "Error searching suggestions", { query }, error);
    }

    // Should be returned in the same order as the index map
    response?.results.forEach((results, index) => {
      const type = indexMap[index].type as SuggestionType;

      this.setSearchResults({
        type,
        results: results as LtkSearchResponse<typeof type>,
        isSuggestion: true,
        locale: this.store.$i18n.locale,
      });
    });
  }

  @Action({ rawError: true })
  async fetchSearch<T extends SearchType>({ query, type }: { query: string; type: T }) {
    const searchSet = this.searchesMap[type];

    const searchParams: SearchProps = {
      rawQuery: query,
      type,
      hitsPerPage: type === "post" ? DEFAULT_POST_LIMIT : DEFAULT_NONPOST_LIMIT,
    };
    const locale = this.store.$i18n.locale;
    const isSameQuery = matchesCaseInsensitive(searchSet.active.query, query, locale);

    if (isSameQuery && this.searchTypeItemHasMorePages(type) === false) {
      return;
    }

    if (isSameQuery) {
      searchParams.offset = searchSet.active.ids.length;
    }

    let results: LtkSearchResponse<T> | undefined;

    try {
      results = (await this.store.$search.search(searchParams)) as LtkSearchResponse<T>;
    } catch (error) {
      // Assume search token is bad, and refresh on initial failure
      logger(logLevel.INFO, "Search Failed", { attempt: 1 }, error);

      try {
        results = (await this.store.$search.search(searchParams)) as LtkSearchResponse<T>;
      } catch (error) {
        logger(logLevel.ERROR, "Search Failed", { attempt: 2 }, error);
      }
    }

    // Search Failed
    if (results === undefined) {
      return;
    }

    this.setSearchResults({ type, results, locale });
  }

  // Mutation Method overloads to narrow type and suggestion combination usage
  setSearchResults<T extends SearchType>(props: SetSearchResultsProps<T>): void;
  setSearchResults<T extends SuggestionType>(props: SetSuggestionResultsProps<T>): void;

  @Mutation
  setSearchResults<T extends QueryableIndex>({
    type,
    results,
    locale,
    isSuggestion = false,
  }: SetSearchResultsBaseProps<T>) {
    const activeSet = (
      isSuggestion ? this.suggestionsMap[type as SuggestionType] : this.searchesMap[type as SearchType]
    ).active;

    const params = new URLSearchParams(results.params);
    const typedQuery = params.get("query") as string;

    const isSameSearch = matchesCaseInsensitive(activeSet.query ?? "", typedQuery, locale);
    let hitMap = mapData("objectID", results.hits);
    // Merge mapped objects when continuing the same search
    if (isSameSearch === true) {
      hitMap = Object.assign({}, activeSet.map, hitMap);
    }

    Vue.set(activeSet, "map", hitMap);

    const length = activeSet.ids.length;
    const start = isSameSearch ? length : 0;
    const end = isSameSearch ? 0 : length;
    const ids = results.hits.map((hit) => hit.objectID);

    activeSet.ids.splice(start, end, ...ids);

    activeSet.indexName = results.indexUsed ?? results.index ?? "";
    activeSet.query = typedQuery;
    activeSet.queryID = results.queryId ?? "";
    activeSet.total = results.nbHits;
  }

  /**
   * Concurrency Locks
   */

  lockSearch(props: { type: SearchType; isSuggestion?: false }): void;
  lockSearch(props: { type: SuggestionType; isSuggestion: true }): void;

  @Mutation
  lockSearch({ type, isSuggestion = false }: { type: QueryableIndex; isSuggestion?: boolean }) {
    const fetchState = (
      isSuggestion ? this.suggestionsMap[type as SuggestionType] : this.searchesMap[type as SearchType]
    ).fetchState;

    fetchState.pending = true;
    fetchState.timestamp = Date.now();
    fetchState.error = null;
  }

  unlockSearch(props: { type: SearchType; isSuggestion?: false }): void;
  unlockSearch(props: { type: SuggestionType; isSuggestion: true }): void;

  @Mutation
  unlockSearch({ type, isSuggestion = false }: { type: QueryableIndex; isSuggestion?: boolean }) {
    const fetchState = (
      isSuggestion ? this.suggestionsMap[type as SuggestionType] : this.searchesMap[type as SearchType]
    ).fetchState;

    fetchState.pending = false;
  }

  /**
   * Pending Queue
   */

  @Mutation
  setQueuedSearch({ type, query }: { type: SearchType; query: string }) {
    this.searchesMap[type].pendingStack.unshift({ query });
  }

  @Mutation
  clearQueuedSearches({ type }: { type: SearchType }) {
    this.searchesMap[type].pendingStack.splice(0, this.searchesMap[type].pendingStack.length);
  }

  @Mutation
  setQueuedSuggestion({ types, query }: { types: SuggestionType[]; query: string }) {
    this.pendingSuggestions.unshift({ types, query });
  }

  @Mutation
  clearQueuedSuggestions() {
    this.pendingSuggestions.splice(0, this.pendingSuggestions.length);
  }

  /**
   * Search Insights Feedback
   */

  /**
   * Action to aggregate store state and pass through to the API, allowing for state overrides
   */
  @Action({ rawError: true })
  resultClick({
    type,
    event,
    objectId,
    position,
    rootReferrer,
    searchExperience,
    index: indexParam,
    queryID: queryIDParam,
  }: PartialSearchClickEvent) {
    const query = this.searchesMap[type].active.query;
    const index = indexParam ?? this.searchesMap[type].active.indexName;
    const queryID = queryIDParam ?? this.searchesMap[type].active.queryID;

    this.store.$search.resultClick({
      index,
      queryID,
      event,
      objectId,
      position,
      type,
      query,
      rootReferrer,
      searchExperience,
    });
  }
}

export const getSearchStore = (store: Store<unknown>) => {
  const searchStore = getModule(SearchStore, store);
  return searchStore;
};
